import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight, Timer, Play, Flag } from "lucide-react";

const SECTIONS = [
  { label: "Quantitative Aptitude", value: "quant", icon: "🔢" },
  { label: "Logical Reasoning", value: "reasoning", icon: "🧩" },
  { label: "Data Interpretation", value: "di", icon: "📊" },
  { label: "Reading Comprehension", value: "rc", icon: "📖" },
  { label: "Decision Making", value: "decision", icon: "⚖️" }
];

const CSATMockTest = ({ isPro, onBack }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Test configuration
  const [testType, setTestType] = useState("sectional"); // full, sectional
  const [selectedSection, setSelectedSection] = useState("quant");
  const [numQuestions, setNumQuestions] = useState(20);
  
  // Test state
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  
  // Timer
  const [timer, setTimer] = useState(0);
  const [testActive, setTestActive] = useState(false);

  // Timer effect
  useEffect(() => {
    let interval;
    if (testActive && !submitted) {
      interval = setInterval(() => setTimer(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [testActive, submitted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate mock test
  const generateTest = async () => {
    setLoading(true);
    toast({ 
      description: "🔄 Generating UPSC-standard mock test... This may take 30-90 seconds.",
      duration: 90000 
    });

    try {
      const startTime = Date.now();
      const { data } = await axios.post(`${API_BASE}/csat/mock-test/generate`, {
        test_type: testType,
        sections: testType === "sectional" ? [selectedSection] : SECTIONS.map(s => s.value),
        num_questions: testType === "full" ? 80 : numQuestions
      });
      const loadTime = ((Date.now() - startTime) / 1000).toFixed(1);

      if (data.questions && data.questions.length > 0) {
        setQuestions(data.questions);
        setCurrentQ(0);
        setAnswers({});
        setSubmitted(false);
        setResults(null);
        setTimer(0);
        setTestActive(true);
        toast({ description: `✅ ${data.num_questions} questions generated in ${loadTime}s. Good luck!` });
      } else {
        toast({ variant: "destructive", description: "No questions generated. Please try again." });
      }
    } catch (err) {
      toast({ 
        variant: "destructive", 
        description: err.response?.data?.detail || "Could not generate mock test." 
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit test
  const submitTest = () => {
    let correct = 0;
    let attempted = 0;
    const sectionWise = {};

    questions.forEach((q, idx) => {
      const userAnswer = answers[idx];
      const section = q.type || 'unknown';
      
      if (!sectionWise[section]) {
        sectionWise[section] = { correct: 0, total: 0, attempted: 0 };
      }
      sectionWise[section].total++;

      if (userAnswer) {
        attempted++;
        sectionWise[section].attempted++;
        if (userAnswer === q.correct_answer) {
          correct++;
          sectionWise[section].correct++;
        }
      }
    });

    const marks = correct * 2 - (attempted - correct) * 0.67;

    setResults({
      correct,
      attempted,
      total: questions.length,
      marks: marks.toFixed(2),
      maxMarks: questions.length * 2,
      percentage: ((correct / questions.length) * 100).toFixed(1),
      sectionWise,
      timeTaken: timer
    });
    setSubmitted(true);
    setTestActive(false);
    toast({ description: `🎯 Test submitted! Score: ${correct}/${questions.length}` });

    // ═══════════════════════════════════════════════════════════════════
    // SINGLE FRONT DOOR — log this CSAT mock attempt to Strategy Analytics
    // ═══════════════════════════════════════════════════════════════════
    const fdToken = localStorage.getItem('token');
    if (fdToken) {
      const loggedQuestions = questions.map((q, idx) => ({
        sub_topic: q.sub_topic || q.topic || q.type || 'unknown',
        is_correct: answers[idx] ? answers[idx] === q.correct_answer : null,
        difficulty: q.difficulty || 'medium',
        time_seconds: 0,
      }));
      axios.post(`${API_BASE}/csat/attempt/log`, {
        source: 'csat_mock',
        section: selectedSection,
        questions: loggedQuestions,
      }, { headers: { Authorization: `Bearer ${fdToken}` } }).catch(() => {});
    }

    // ═══════════════════════════════════════════════════════════════════
    // TRACK WRONG ANSWERS TO MISTAKE BANK
    // ═══════════════════════════════════════════════════════════════════
    const token = localStorage.getItem('token');
    if (token) {
      questions.forEach(async (q, idx) => {
        const userAnswer = answers[idx];
        if (userAnswer && userAnswer !== q.correct_answer) {
          try {
            await axios.post(`${API_BASE}/mistakes/add`, {
              question_id: `csat-mock-${selectedSection}-${Date.now()}-${idx}`,
              question: q.question,
              options: q.options,
              correct_option: ['A', 'B', 'C', 'D'].indexOf(q.correct_answer),
              explanation: q.explanation || '',
              user_answer: ['A', 'B', 'C', 'D'].indexOf(userAnswer),
              source: 'csat_mock',
              subject: `CSAT - ${selectedSection}`
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            // Silent fail
          }
        }
      });
    }
  };

  const currentQuestion = questions[currentQ];

  // Test Configuration View - CLARIAS Style
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={onBack}>← Back to CSAT Suite</Button>
          <Badge variant="outline" className="px-4 py-2 text-sm border-slate-900 text-slate-900">PRO</Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">Mock & Sectional Tests</h1>
          <p className="text-slate-500">
            Full 80-Q mocks • Sectional practice • UPSC-standard questions
          </p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-6">
            {/* Test Type */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700">Test Type</label>
              <div className="flex gap-4">
                <Button
                  variant={testType === "full" ? "default" : "outline"}
                  onClick={() => setTestType("full")}
                  className={testType === "full" ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
                >
                  Full Mock (80Q)
                </Button>
                <Button
                  variant={testType === "sectional" ? "default" : "outline"}
                  onClick={() => setTestType("sectional")}
                  className={testType === "sectional" ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
                >
                  Sectional Test
                </Button>
              </div>
            </div>

            {/* Section Selection (Sectional only) */}
            {testType === "sectional" && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Select Section</label>
                <div className="flex flex-wrap gap-2">
                  {SECTIONS.map((section) => (
                    <Button
                      key={section.value}
                      variant={selectedSection === section.value ? "default" : "outline"}
                      onClick={() => setSelectedSection(section.value)}
                      className={selectedSection === section.value ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
                      size="sm"
                    >
                      {section.icon} {section.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Number of Questions (Sectional only) */}
            {testType === "sectional" && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Number of Questions</label>
                <div className="flex gap-2">
                  {[10, 20, 30].map((n) => (
                    <Button
                      key={n}
                      variant={numQuestions === n ? "default" : "outline"}
                      onClick={() => setNumQuestions(n)}
                      className={numQuestions === n ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
                      size="sm"
                    >
                      {n} Questions
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Test Info */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-slate-500 font-medium">QUESTIONS</p>
                  <p className="text-xl font-semibold text-slate-900">{testType === "full" ? 80 : numQuestions}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">DURATION</p>
                  <p className="text-xl font-semibold text-slate-900">{testType === "full" ? "120" : "30"} min</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">MARKS</p>
                  <p className="text-xl font-semibold text-slate-900">{(testType === "full" ? 80 : numQuestions) * 2}</p>
                </div>
              </div>
            </div>

            {/* Start Button */}
            <Button
              onClick={generateTest}
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating Questions...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start {testType === "full" ? "Full Mock" : "Sectional"} Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
      </div>
    );
  }

  // Test Taking View
  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      {/* Header with Timer */}
      <div className="sticky top-0 bg-white z-10 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Badge className="bg-violet-100 text-violet-800">
            Q {currentQ + 1} / {questions.length}
          </Badge>
          {currentQuestion?.type && (
            <Badge variant="outline">
              {SECTIONS.find(s => s.value === currentQuestion.type)?.label || currentQuestion.type}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-violet-900 font-mono font-bold">
            <Timer className="w-5 h-5" />
            {formatTime(timer)}
          </div>
          {!submitted && (
            <Button
              onClick={submitTest}
              variant="destructive"
              size="sm"
            >
              <Flag className="w-4 h-4 mr-1" /> Submit
            </Button>
          )}
        </div>
      </div>

      {/* Question Navigator */}
      <div className="flex flex-wrap gap-1 justify-center max-h-24 overflow-y-auto py-2">
        {questions.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentQ(idx)}
            className={`w-7 h-7 rounded text-xs font-bold transition-all ${
              currentQ === idx
                ? "bg-violet-600 text-white"
                : answers[idx]
                  ? submitted
                    ? answers[idx] === questions[idx].correct_answer
                      ? "bg-green-200 text-green-800"
                      : "bg-red-200 text-red-800"
                    : "bg-violet-100 text-violet-800"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {/* Question Card — keyed by currentQ so it fully remounts on navigation,
          preventing Google Translate from leaving stale translated option text. */}
      {currentQuestion && (
        <Card key={currentQ} className="border-violet-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Badge className={`${
                currentQuestion.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                currentQuestion.difficulty === 'hard' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {currentQuestion.difficulty || 'Medium'}
              </Badge>
              {currentQuestion.time_recommended && (
                <span className="text-xs text-slate-500">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {currentQuestion.time_recommended}
                </span>
              )}
            </div>

            {/* Passage (for RC) */}
            {currentQuestion.passage && (
              <div className="mb-4 p-4 bg-slate-50 rounded-lg border text-sm text-slate-700 max-h-48 overflow-y-auto">
                {currentQuestion.passage}
              </div>
            )}

            <p className="text-lg font-medium text-slate-900 mb-6">
              {currentQuestion.question}
            </p>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options?.map((option, optIdx) => {
                const optionLetter = option.charAt(0);
                const isSelected = answers[currentQ] === optionLetter;
                const isCorrect = optionLetter === currentQuestion.correct_answer;

                return (
                  <button
                    key={optIdx}
                    onClick={() => {
                      if (!submitted) {
                        setAnswers({ ...answers, [currentQ]: optionLetter });
                      }
                    }}
                    disabled={submitted}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      submitted && isCorrect
                        ? "border-green-500 bg-green-50"
                        : submitted && isSelected && !isCorrect
                          ? "border-red-500 bg-red-50"
                          : isSelected
                            ? "border-violet-500 bg-violet-50"
                            : "border-slate-200 hover:border-violet-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{option}</span>
                      {submitted && isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                      {submitted && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Explanation (after submit) */}
            {submitted && currentQuestion.explanation && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-semibold text-blue-700 mb-2">EXPLANATION</p>
                <p className="text-sm text-slate-800">{currentQuestion.explanation}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
          disabled={currentQ === 0}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>

        <Button
          variant="outline"
          onClick={() => setCurrentQ(Math.min(questions.length - 1, currentQ + 1))}
          disabled={currentQ === questions.length - 1}
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Results Card */}
      {submitted && results && (
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-violet-900 mb-4 text-center">🎯 Test Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-violet-600 font-semibold">SCORE</p>
                <p className="text-2xl font-bold text-violet-900">{results.correct}/{results.total}</p>
              </div>
              <div>
                <p className="text-xs text-violet-600 font-semibold">MARKS</p>
                <p className="text-2xl font-bold text-violet-900">{results.marks}/{results.maxMarks}</p>
              </div>
              <div>
                <p className="text-xs text-violet-600 font-semibold">ACCURACY</p>
                <p className="text-2xl font-bold text-violet-900">{results.percentage}%</p>
              </div>
              <div>
                <p className="text-xs text-violet-600 font-semibold">TIME</p>
                <p className="text-2xl font-bold text-violet-900">{formatTime(results.timeTaken)}</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <Button onClick={onBack} className="bg-violet-600 hover:bg-violet-700">
                Back to CSAT Suite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CSATMockTest;
